import { doc, getFirestore, runTransaction } from "firebase/firestore";
import {
  construirActualizacionRutaConMovimiento,
  crearMovimientoRutaData,
  crearMovimientoRutaRef,
} from "./rutaMovimientos";

type ClientePagado = Record<string, any> & {
  uid?: string;
};

type ProcesarClientesPagadosParams<T extends ClientePagado> = {
  adminUid: string;
  clientes: T[];
  fechaReferencia?: Date;
  rutaUid: string;
};

type ResultadoProcesarClientesPagados<T extends ClientePagado> = {
  clientesActivos: T[];
  clientesEliminados: string[];
};

const normalizarNumero = (value: unknown): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const normalizarFecha = (value: unknown): Date | null => {
  const valueAsObject =
    typeof value === "object" && value !== null ? (value as Record<string, any>) : null;

  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (valueAsObject && typeof valueAsObject.toDate === "function") {
    const fecha = valueAsObject.toDate() as Date;
    return Number.isNaN(fecha.getTime()) ? null : fecha;
  }

  if (valueAsObject && typeof valueAsObject.toMillis === "function") {
    const fecha = new Date(valueAsObject.toMillis());
    return Number.isNaN(fecha.getTime()) ? null : fecha;
  }

  if (typeof value !== "string" && typeof value !== "number") {
    return null;
  }

  const fecha = new Date(value);
  return Number.isNaN(fecha.getTime()) ? null : fecha;
};

export const obtenerUltimaFechaPago = (cliente: ClientePagado): Date | null => {
  if (!Array.isArray(cliente.historialPagos) || !cliente.historialPagos.length) {
    return null;
  }

  let ultimaFecha: Date | null = null;

  cliente.historialPagos.forEach((pago: ClientePagado) => {
    const fechaPago = normalizarFecha(pago?.fecha);

    if (fechaPago && (!ultimaFecha || fechaPago.getTime() > ultimaFecha.getTime())) {
      ultimaFecha = fechaPago;
    }
  });

  return ultimaFecha;
};

export const debeEliminarPorFecha = (ultimaFechaPago: Date | null, fechaHoy: Date): boolean => {
  if (!ultimaFechaPago) {
    return false;
  }

  const fechaLimiteEliminacion = new Date(ultimaFechaPago.getTime() + 24 * 60 * 60 * 1000);
  fechaLimiteEliminacion.setHours(0, 0, 0, 0);

  return fechaHoy >= fechaLimiteEliminacion;
};

export const debeEliminarClientePagado = (cliente: ClientePagado): boolean => {
  if (!Array.isArray(cliente.historialPagos) || !cliente.historialPagos.length) {
    return false;
  }

  const cuotasPactadas = normalizarNumero(cliente.cuotasPactadas);
  const cuotasPagadas = normalizarNumero(cliente.cuotasPagadas);
  const pagoDiario = normalizarNumero(cliente.pagoDiario);
  const totalAbono = normalizarNumero(cliente.totalAbono);
  const deudaTotal = cuotasPactadas * pagoDiario;

  return (
    (cuotasPactadas > 0 && cuotasPagadas >= cuotasPactadas) ||
    (deudaTotal > 0 && totalAbono >= deudaTotal)
  );
};

export const debeEliminarClientePagadoEnFecha = (
  cliente: ClientePagado,
  fechaReferencia: Date,
): boolean =>
  debeEliminarClientePagado(cliente) &&
  debeEliminarPorFecha(obtenerUltimaFechaPago(cliente), fechaReferencia);

export const procesarClientesPagadosDeRuta = async <T extends ClientePagado>({
  adminUid,
  clientes,
  fechaReferencia = new Date(),
  rutaUid,
}: ProcesarClientesPagadosParams<T>): Promise<ResultadoProcesarClientesPagados<T>> => {
  const clientesAEliminar = clientes.filter(
    (cliente) => Boolean(cliente.uid) && debeEliminarClientePagadoEnFecha(cliente, fechaReferencia),
  );

  if (!clientesAEliminar.length) {
    return {
      clientesActivos: clientes,
      clientesEliminados: [],
    };
  }

  const db = getFirestore();
  const rutaRef = doc(db, "admin_users", adminUid, "rutas", rutaUid);
  const resultados = await Promise.allSettled(
    clientesAEliminar.map(async (cliente) => {
      const clienteUid = String(cliente.uid);
      const clienteRef = doc(db, "admin_users", adminUid, "rutas", rutaUid, "clientes", clienteUid);
      const movimientoRef = crearMovimientoRutaRef(db, adminUid, rutaUid);

      return runTransaction(db, async (transaction): Promise<string | null> => {
        const clienteSnapshot = await transaction.get(clienteRef);

        if (!clienteSnapshot.exists()) {
          return clienteUid;
        }

        const clienteActual: ClientePagado & { uid: string } = {
          uid: clienteSnapshot.id,
          ...clienteSnapshot.data(),
        };

        if (!debeEliminarClientePagadoEnFecha(clienteActual, fechaReferencia)) {
          return null;
        }

        const rutaSnapshot = await transaction.get(rutaRef);
        const rutaData = rutaSnapshot.data();

        if (!rutaSnapshot.exists() || !rutaData) {
          throw new Error("No se pudo obtener la ruta para eliminar el cliente pagado.");
        }

        const saldoActual = normalizarNumero(rutaData.saldoInicial);
        const movimiento = crearMovimientoRutaData({
          clienteUid: clienteActual.uid,
          descripcion: "Cliente eliminado por deuda pagada",
          fecha: fechaReferencia,
          monto: 0,
          origen: "eliminar_cliente_pago_completado",
          responsable: String(clienteActual.nombreCliente ?? "Sin datos"),
          saldoDespues: saldoActual,
          tipo: "ingreso",
        });
        const rutaUpdates = construirActualizacionRutaConMovimiento(
          rutaData,
          movimiento,
          saldoActual,
        );

        transaction.set(movimientoRef, movimiento);
        transaction.update(rutaRef, rutaUpdates);
        transaction.delete(clienteRef);

        return clienteActual.uid;
      });
    }),
  );

  const clientesEliminados = resultados.flatMap((resultado) => {
    if (resultado.status === "rejected") {
      console.error("Error al eliminar cliente", resultado.reason);
      return [];
    }

    return resultado.value ? [resultado.value] : [];
  });
  const clientesEliminadosSet = new Set(clientesEliminados);

  return {
    clientesActivos: clientes.filter(
      (cliente) => !cliente.uid || !clientesEliminadosSet.has(cliente.uid),
    ),
    clientesEliminados,
  };
};
